import { useEffect, useState } from "react";
import AppLayout from "@/components/layout/AppLayout";
import { apiPrivate } from "@/lib/api";
import { Plus, Loader2, Calendar, MapPin, Video, Trash2, Send, Lock, Download, Users, X, ListChecks, Search, AlertTriangle, Mail, FileText } from "lucide-react";
import { toast } from "sonner";

const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleString("it-IT", { dateStyle: "medium", timeStyle: "short" }); }
    catch { return iso; }
};

const STATUS_META = {
    draft: { label: "Bozza", chip: "bg-zinc-700/40 text-zinc-300" },
    published: { label: "Pubblicata", chip: "bg-bayside/15 text-bayside" },
    closed: { label: "Chiusa", chip: "bg-zinc-700/40 text-zinc-400" },
};

export default function AdminAssemblies() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editing, setEditing] = useState(null);
    const [detailTarget, setDetailTarget] = useState(null);
    const [publishTarget, setPublishTarget] = useState(null);

    const load = () => {
        setLoading(true);
        apiPrivate.get("/admin/assemblies")
            .then(r => setItems(r.data || []))
            .finally(() => setLoading(false));
    };
    useEffect(() => { load(); }, []);

    const openPublish = (assembly) => setPublishTarget(assembly);

    const close = async (id) => {
        if (!window.confirm("Chiudere definitivamente l'assemblea? Gli RSVP non saranno più modificabili.")) return;
        await apiPrivate.post(`/admin/assemblies/${id}/close`);
        toast.success("Assemblea chiusa");
        load();
    };

    const del = async (id) => {
        if (!window.confirm("Eliminare la bozza? Operazione irreversibile.")) return;
        try {
            await apiPrivate.delete(`/admin/assemblies/${id}`);
            toast.success("Assemblea eliminata");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore");
        }
    };

    return (
        <AppLayout admin>
            <div className="max-w-[1400px] mx-auto" data-testid="admin-assemblies">
                <div className="flex items-end justify-between gap-4 flex-wrap">
                    <div>
                        <span className="label-tag text-magenta">Assemblee</span>
                        <h1 className="font-display text-5xl mt-3 tracking-tighter">
                            Convoca, raccogli, <span className="font-serif-italic text-gradient-tropical">delibera</span>.
                        </h1>
                        <p className="text-zinc-400 mt-3 max-w-2xl">
                            Crea assemblee ordinarie o straordinarie, invia convocazioni con calendario allegato (.ics), monitora RSVP e deleghe in tempo reale.
                        </p>
                    </div>
                    <button onClick={() => { setEditing(null); setShowForm(true); }} className="btn-rebel" data-testid="new-assembly-btn">
                        <Plus className="h-4 w-4" /> Nuova assemblea
                    </button>
                </div>

                {loading ? (
                    <div className="bento p-12 text-center text-zinc-500 mt-8"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
                ) : items.length === 0 ? (
                    <div className="bento p-12 text-center text-zinc-500 mt-8" data-testid="assemblies-empty">
                        <Calendar className="h-10 w-10 mx-auto mb-3 opacity-40" />
                        Nessuna assemblea creata. Clicca &quot;Nuova assemblea&quot; per iniziare.
                    </div>
                ) : (
                    <div className="space-y-3 mt-8">
                        {items.map(a => {
                            const meta = STATUS_META[a.status];
                            const s = a.rsvp_summary || {};
                            return (
                                <div key={a.id} className="bento p-5" data-testid={`assembly-row-${a.id}`}>
                                    <div className="flex items-start justify-between gap-4 flex-wrap">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className={`px-2 py-0.5 rounded-full text-xs ${meta.chip}`}>{meta.label}</span>
                                                <span className="px-2 py-0.5 rounded-full text-xs bg-magenta/15 text-magenta">{a.type}</span>
                                                <span className="text-xs text-zinc-500">{fmtDate(a.date_time)} · {a.duration_minutes}min</span>
                                            </div>
                                            <h3 className="text-xl font-display tracking-tighter mt-2">{a.title}</h3>
                                            <div className="flex items-center gap-4 mt-2 text-xs text-zinc-500 flex-wrap">
                                                {a.location && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {a.location}</span>}
                                                {a.videocall_url && <span className="flex items-center gap-1"><Video className="h-3 w-3" /> Link videocall</span>}
                                                {a.agenda?.length > 0 && <span className="flex items-center gap-1"><ListChecks className="h-3 w-3" /> {a.agenda.length} punti OdG</span>}
                                            </div>
                                            {a.status !== "draft" && (
                                                <div className="flex items-center gap-3 mt-3 text-xs flex-wrap">
                                                    <span className="text-bayside">● Presenti: <strong>{s.presente || 0}</strong></span>
                                                    <span className="text-magenta">● Deleghe: <strong>{s.delegato || 0}</strong></span>
                                                    <span className="text-vermillion">● Assenti: <strong>{s.assente || 0}</strong></span>
                                                    <span className="text-zinc-500">● Senza risposta: <strong>{s.no_response || 0}</strong> / {s.total_socio}</span>
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex gap-2 flex-wrap">
                                            <button onClick={() => setDetailTarget(a)} className="btn-ghost text-xs" data-testid={`detail-btn-${a.id}`}>
                                                <Users className="h-3.5 w-3.5" /> RSVP
                                            </button>
                                            {a.status === "draft" && (
                                                <>
                                                    <button onClick={() => { setEditing(a); setShowForm(true); }} className="btn-ghost text-xs" data-testid={`edit-btn-${a.id}`}>Modifica</button>
                                                    <button onClick={() => openPublish(a)} className="btn-ghost text-xs text-bayside" data-testid={`publish-btn-${a.id}`}>
                                                        <Send className="h-3.5 w-3.5" /> Pubblica
                                                    </button>
                                                    <button onClick={() => del(a.id)} className="btn-ghost text-xs text-vermillion" data-testid={`delete-btn-${a.id}`}>
                                                        <Trash2 className="h-3.5 w-3.5" />
                                                    </button>
                                                </>
                                            )}
                                            {(a.status === "published" || a.status === "closed") && (
                                                <VerbaleInlineBtn assembly={a} onDone={load} />
                                            )}
                                            {a.status === "published" && (
                                                <button onClick={() => close(a.id)} className="btn-ghost text-xs text-zinc-400" data-testid={`close-btn-${a.id}`}>
                                                    <Lock className="h-3.5 w-3.5" /> Chiudi
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {showForm && <AssemblyForm initial={editing} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
                {detailTarget && <RsvpDetail assembly={detailTarget} onClose={() => setDetailTarget(null)} />}
                {publishTarget && (
                    <PublishModal
                        assembly={publishTarget}
                        onClose={() => setPublishTarget(null)}
                        onPublished={() => { setPublishTarget(null); load(); }}
                    />
                )}
            </div>
        </AppLayout>
    );
}

function AssemblyForm({ initial, onClose, onSaved }) {
    const [form, setForm] = useState(() => ({
        title: initial?.title || "",
        type: initial?.type || "ordinaria",
        date_time: initial?.date_time ? initial.date_time.slice(0, 16) : "",
        duration_minutes: initial?.duration_minutes || 120,
        mode: initial?.mode || "ibrida",
        location: initial?.location || "",
        videocall_url: initial?.videocall_url || "",
        description: initial?.description || "",
        agenda: initial?.agenda || [],
        rsvp_deadline: initial?.rsvp_deadline ? initial.rsvp_deadline.slice(0, 16) : "",
    }));
    const [saving, setSaving] = useState(false);

    const set = (k, v) => setForm({ ...form, [k]: v });
    const addItem = () => set("agenda", [...form.agenda, { id: crypto.randomUUID(), title: "", order: form.agenda.length + 1 }]);
    const updItem = (i, k, v) => set("agenda", form.agenda.map((it, idx) => idx === i ? { ...it, [k]: v } : it));
    const delItem = (i) => set("agenda", form.agenda.filter((_, idx) => idx !== i));

    const submit = async () => {
        if (!form.title || !form.date_time) {
            toast.error("Titolo e data sono obbligatori");
            return;
        }
        setSaving(true);
        try {
            const payload = {
                ...form,
                date_time: new Date(form.date_time).toISOString(),
                rsvp_deadline: form.rsvp_deadline ? new Date(form.rsvp_deadline).toISOString() : "",
                agenda: form.agenda.filter(a => a.title.trim()),
            };
            if (initial) {
                await apiPrivate.patch(`/admin/assemblies/${initial.id}`, payload);
                toast.success("Assemblea aggiornata");
            } else {
                await apiPrivate.post("/admin/assemblies", payload);
                toast.success("Assemblea creata in bozza");
            }
            onSaved();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore salvataggio");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="assembly-form-modal">
            <div className="bento p-6 max-w-2xl w-full my-8" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-2xl font-display tracking-tighter">{initial ? "Modifica" : "Nuova"} assemblea</h3>
                    <button onClick={onClose} className="text-zinc-500 hover:text-white"><X className="h-5 w-5" /></button>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                        <label className="text-xs text-zinc-500">Titolo *</label>
                        <input value={form.title} onChange={e => set("title", e.target.value)} className="input-field" data-testid="form-title" />
                    </div>
                    <div>
                        <label className="text-xs text-zinc-500">Tipo</label>
                        <select value={form.type} onChange={e => set("type", e.target.value)} className="input-field" data-testid="form-type">
                            <option value="ordinaria">Ordinaria</option>
                            <option value="straordinaria">Straordinaria</option>
                        </select>
                    </div>
                    <div>
                        <label className="text-xs text-zinc-500">Modalità</label>
                        <select value={form.mode} onChange={e => set("mode", e.target.value)} className="input-field" data-testid="form-mode">
                            <option value="presenza">Solo presenza</option>
                            <option value="remoto">Solo remoto</option>
                            <option value="ibrida">Ibrida</option>
                        </select>
                    </div>
                    <div>
                        <label className="text-xs text-zinc-500">Data e ora *</label>
                        <input type="datetime-local" value={form.date_time} onChange={e => set("date_time", e.target.value)} className="input-field" data-testid="form-date" />
                    </div>
                    <div>
                        <label className="text-xs text-zinc-500">Durata (min)</label>
                        <input type="number" min={15} max={600} value={form.duration_minutes} onChange={e => set("duration_minutes", Number(e.target.value))} className="input-field" data-testid="form-duration" />
                    </div>
                    <div className="md:col-span-2">
                        <label className="text-xs text-zinc-500">Termine ultimo per rispondere (RSVP)</label>
                        <input
                            type="datetime-local"
                            value={form.rsvp_deadline}
                            onChange={e => set("rsvp_deadline", e.target.value)}
                            max={form.date_time || undefined}
                            className="input-field"
                            data-testid="form-rsvp-deadline"
                        />
                        <p className="text-[10px] text-zinc-600 mt-1 font-grotesk">Se lasciato vuoto, il termine coincide con la data dell'assemblea.</p>
                    </div>
                    <div className="md:col-span-2">
                        <label className="text-xs text-zinc-500">Indirizzo (presenza)</label>
                        <input value={form.location} onChange={e => set("location", e.target.value)} placeholder="Es. Sede sociale, Via X 1, Milano" className="input-field" data-testid="form-location" />
                    </div>
                    <div className="md:col-span-2">
                        <label className="text-xs text-zinc-500">Link videocall (Meet/Teams/Zoom)</label>
                        <input value={form.videocall_url} onChange={e => set("videocall_url", e.target.value)} placeholder="https://..." className="input-field" data-testid="form-videocall" />
                    </div>
                    <div className="md:col-span-2">
                        <label className="text-xs text-zinc-500">Descrizione</label>
                        <textarea value={form.description} onChange={e => set("description", e.target.value)} rows={3} className="input-field" data-testid="form-description" />
                    </div>
                </div>

                {/* Agenda */}
                <div className="mt-6">
                    <div className="flex items-center justify-between">
                        <label className="text-xs text-zinc-500 uppercase tracking-widest">Ordine del giorno</label>
                        <button onClick={addItem} className="btn-ghost text-xs" data-testid="add-agenda-item">
                            <Plus className="h-3 w-3" /> Punto
                        </button>
                    </div>
                    <div className="space-y-2 mt-2">
                        {form.agenda.length === 0 ? (
                            <p className="text-xs text-zinc-600 italic">Nessun punto inserito (opzionale).</p>
                        ) : form.agenda.map((it, i) => (
                            <div key={it.id} className="flex items-center gap-2" data-testid={`agenda-item-${i}`}>
                                <span className="text-xs text-zinc-500 w-6">{i + 1}.</span>
                                <input value={it.title} onChange={e => updItem(i, "title", e.target.value)} placeholder="Titolo del punto" className="input-field flex-1 !mt-0" />
                                <button onClick={() => delItem(i)} className="text-vermillion hover:text-vermillion/80 p-2"><Trash2 className="h-3.5 w-3.5" /></button>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Allegati: PDF convocazione + verbale (solo per assemblee salvate) */}
                {initial && (
                    <AttachmentsSection assembly={initial} />
                )}

                <div className="flex justify-end gap-2 mt-6">
                    <button onClick={onClose} className="btn-ghost">Annulla</button>
                    <button onClick={submit} disabled={saving} className="btn-rebel" data-testid="form-submit">
                        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                        {initial ? "Salva modifiche" : "Crea bozza"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function AttachmentsSection({ assembly }) {
    const [uploading, setUploading] = useState(false);
    const [uploadingVerbale, setUploadingVerbale] = useState(false);
    const [pdf, setPdf] = useState({ key: assembly.convocation_pdf_key, filename: assembly.convocation_pdf_filename });
    const [verbaleId, setVerbaleId] = useState(assembly.verbale_document_id || "");
    const [availableDocs, setAvailableDocs] = useState([]);

    const loadDocs = () => {
        apiPrivate.get("/admin/documents?category=verbali_assemblea").then(r => setAvailableDocs(r.data || [])).catch(() => { });
    };
    useEffect(() => { loadDocs(); }, []);

    const uploadPdf = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const r = await apiPrivate.post(`/admin/assemblies/${assembly.id}/convocation-pdf`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            setPdf({ key: r.data.key, filename: r.data.filename });
            toast.success("PDF convocazione caricato");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Errore upload");
        } finally {
            setUploading(false);
            e.target.value = "";
        }
    };

    const removePdf = async () => {
        if (!window.confirm("Rimuovere il PDF convocazione?")) return;
        try {
            await apiPrivate.delete(`/admin/assemblies/${assembly.id}/convocation-pdf`);
            setPdf({ key: null, filename: null });
            toast.success("PDF rimosso");
        } catch (e) { toast.error("Errore"); }
    };

    const linkVerbale = async (docId) => {
        try {
            await apiPrivate.post(`/admin/assemblies/${assembly.id}/verbale-link`, { document_id: docId || null });
            setVerbaleId(docId);
            toast.success(docId ? "Verbale collegato" : "Collegamento rimosso");
        } catch (e) { toast.error("Errore"); }
    };

    const uploadVerbale = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploadingVerbale(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const r = await apiPrivate.post(`/admin/assemblies/${assembly.id}/verbale-upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            setVerbaleId(r.data.document_id);
            toast.success("Verbale caricato e collegato — visibile ai soci in Documenti");
            loadDocs();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Errore upload verbale");
        } finally {
            setUploadingVerbale(false);
            e.target.value = ""; // permette re-upload stesso file
        }
    };

    const currentVerbale = availableDocs.find(d => d.id === verbaleId);

    return (
        <div className="mt-6 pt-6 border-t border-white/10 space-y-5">
            <h4 className="text-sm uppercase tracking-widest text-zinc-500 font-grotesk">Allegati</h4>
            <div>
                <label className="text-xs text-zinc-500 uppercase tracking-widest">Convocazione PDF ufficiale</label>
                {pdf.key ? (
                    <div className="mt-2 flex items-center justify-between gap-3 p-3 rounded-xl bg-zinc-900/40 border border-zinc-800">
                        <div className="text-sm text-zinc-300 truncate">📄 {pdf.filename}</div>
                        <button onClick={removePdf} className="text-vermillion hover:text-vermillion/80 text-xs" data-testid="remove-pdf-btn">Rimuovi</button>
                    </div>
                ) : (
                    <div className="mt-2">
                        <label className="btn-ghost cursor-pointer inline-flex items-center gap-2" data-testid="upload-pdf-btn">
                            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                            Carica PDF (max 20 MB)
                            <input type="file" accept="application/pdf" className="hidden" onChange={uploadPdf} data-testid="upload-pdf-input" />
                        </label>
                    </div>
                )}
            </div>

            {/* VERBALE — Upload diretto */}
            <div className="rounded-xl bg-zinc-900/30 border border-zinc-800 p-4" data-testid="verbale-block">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                        <label className="text-xs text-zinc-500 uppercase tracking-widest">Verbale dell'assemblea</label>
                        <p className="text-[11px] text-zinc-600 mt-1 max-w-md font-grotesk">
                            Il PDF caricato qui viene pubblicato automaticamente in <span className="text-bayside">Documenti → Verbali Assemblea</span> per tutti i soci e collegato allo storico convocazioni.
                        </p>
                    </div>
                    <label className="btn-rebel cursor-pointer inline-flex items-center gap-2 shrink-0" data-testid="upload-verbale-btn">
                        {uploadingVerbale ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                        {currentVerbale ? "Sostituisci verbale" : "Carica verbale PDF"}
                        <input type="file" accept="application/pdf" className="hidden" onChange={uploadVerbale} data-testid="upload-verbale-input" />
                    </label>
                </div>

                {currentVerbale ? (
                    <div className="mt-3 flex items-center justify-between gap-3 p-3 rounded-lg bg-zinc-950/40" data-testid="verbale-current">
                        <div className="min-w-0">
                            <div className="text-sm text-white truncate">📎 {currentVerbale.title}</div>
                            <div className="text-[11px] text-zinc-500 font-grotesk">{currentVerbale.filename} · pubblicato {new Date(currentVerbale.published_at).toLocaleDateString("it-IT")}</div>
                        </div>
                        <button onClick={() => linkVerbale("")} className="text-vermillion text-xs hover:text-vermillion/80" data-testid="unlink-verbale-btn">Scollega</button>
                    </div>
                ) : (
                    <div className="mt-3 text-xs text-zinc-500 italic">Nessun verbale collegato.</div>
                )}

                {/* Fallback: collega verbale già presente in Documenti */}
                <div className="mt-4">
                    <label className="text-[10px] text-zinc-600 uppercase tracking-widest font-grotesk">Oppure collega un verbale già pubblicato</label>
                    <select value={verbaleId} onChange={e => linkVerbale(e.target.value)} className="input-field mt-1" data-testid="verbale-select">
                        <option value="">— Nessun verbale collegato —</option>
                        {availableDocs.map(d => (
                            <option key={d.id} value={d.id}>{d.title} · {new Date(d.published_at).toLocaleDateString("it-IT")}</option>
                        ))}
                    </select>
                </div>
            </div>
        </div>
    );
}

function RsvpDetail({ assembly, onClose }) {
    const [rsvps, setRsvps] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        apiPrivate.get(`/admin/assemblies/${assembly.id}/rsvps`)
            .then(r => setRsvps(r.data || []))
            .finally(() => setLoading(false));
    }, [assembly.id]);

    const downloadCsv = () => {
        const rows = [["Socio", "Email", "Quote", "Stato", "Delega a", "Aggiornato", "Note"]];
        for (const r of rsvps) {
            rows.push([r.user_display, r.user_email, (r.quota_categories || []).join("/"), r.status, r.delegate_display || "", r.updated_at, r.note || ""]);
        }
        const csv = rows.map(row => row.map(c => `"${(c ?? "").toString().replace(/"/g, '""')}"`).join(",")).join("\n");
        const blob = new Blob([csv], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `rsvps-${assembly.id.slice(0, 8)}.csv`;
        link.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="rsvp-modal">
            <div className="bento p-6 max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
                <div className="flex items-start justify-between gap-4 mb-4">
                    <div>
                        <h3 className="text-2xl font-display tracking-tighter">{assembly.title}</h3>
                        <p className="text-xs text-zinc-500 mt-1">{fmtDate(assembly.date_time)} · {STATUS_META[assembly.status].label}</p>
                    </div>
                    <div className="flex gap-2">
                        <button onClick={downloadCsv} className="btn-ghost text-xs" data-testid="export-csv-btn">
                            <Download className="h-3 w-3" /> CSV
                        </button>
                        <button onClick={onClose} className="text-zinc-500 hover:text-white"><X className="h-5 w-5" /></button>
                    </div>
                </div>

                {loading ? (
                    <div className="p-12 text-center text-zinc-500"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
                ) : rsvps.length === 0 ? (
                    <div className="p-12 text-center text-zinc-500">Nessun RSVP ricevuto ancora.</div>
                ) : (
                    <div className="overflow-y-auto">
                        <table className="w-full text-sm">
                            <thead className="text-xs text-zinc-500 uppercase tracking-widest sticky top-0 bg-[#0a0c1c]">
                                <tr className="border-b border-zinc-800">
                                    <th className="text-left py-2">Socio</th>
                                    <th className="text-left py-2">Quote</th>
                                    <th className="text-left py-2">Stato</th>
                                    <th className="text-left py-2">Delega a</th>
                                    <th className="text-left py-2">Aggiornato</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rsvps.map(r => (
                                    <tr key={r.user_id} className="border-b border-zinc-900/60" data-testid={`rsvp-row-${r.user_id}`}>
                                        <td className="py-2">
                                            <div className="font-semibold">{r.user_display}</div>
                                            <div className="text-xs text-zinc-500">{r.user_email}</div>
                                        </td>
                                        <td className="py-2">
                                            <div className="flex gap-1">
                                                {(r.quota_categories || []).map(q => <span key={q} className="px-1.5 py-0.5 rounded-full bg-bayside/15 text-bayside text-xs">{q}</span>)}
                                            </div>
                                        </td>
                                        <td className="py-2">
                                            <span className={`px-2 py-0.5 rounded-full text-xs ${r.status === "presente" ? "bg-bayside/15 text-bayside" : r.status === "delegato" ? "bg-magenta/15 text-magenta" : "bg-vermillion/15 text-vermillion"}`}>
                                                {r.status}
                                            </span>
                                        </td>
                                        <td className="py-2 text-zinc-300">{r.delegate_display || "—"}</td>
                                        <td className="py-2 text-xs text-zinc-500">{fmtDate(r.updated_at)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}


function PublishModal({ assembly, onClose, onPublished }) {
    const [recipients, setRecipients] = useState([]);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [loading, setLoading] = useState(true);
    const [publishing, setPublishing] = useState(false);
    const [search, setSearch] = useState("");
    const [quotaFilter, setQuotaFilter] = useState("all");

    useEffect(() => {
        apiPrivate.get(`/admin/assemblies/${assembly.id}/publish-recipients`)
            .then(r => {
                setRecipients(r.data || []);
                setSelectedIds(new Set((r.data || []).filter(u => !u.email_bounced).map(u => u.id)));
            })
            .catch(() => toast.error("Errore caricamento destinatari"))
            .finally(() => setLoading(false));
    }, [assembly.id]);

    const filtered = recipients.filter(r => {
        if (search && !(`${r.nome} ${r.email}`.toLowerCase().includes(search.toLowerCase()))) return false;
        if (quotaFilter !== "all" && !r.quota_categories.includes(quotaFilter)) return false;
        return true;
    });

    const toggle = (id) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };
    const selectAllVisible = () => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            filtered.forEach(r => next.add(r.id));
            return next;
        });
    };
    const deselectAllVisible = () => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            filtered.forEach(r => next.delete(r.id));
            return next;
        });
    };
    const resetToActive = () => {
        setSelectedIds(new Set(recipients.filter(u => !u.email_bounced).map(u => u.id)));
    };

    const publish = async () => {
        const ids = Array.from(selectedIds);
        if (ids.length === 0) { toast.error("Seleziona almeno un destinatario"); return; }
        setPublishing(true);
        try {
            const r = await apiPrivate.post(`/admin/assemblies/${assembly.id}/publish`, { user_ids: ids });
            toast.success(`Convocazione inviata a ${r.data.recipients_count} soci`);
            onPublished();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore pubblicazione");
        } finally { setPublishing(false); }
    };

    const bouncedCount = recipients.filter(r => r.email_bounced).length;
    const selectedCount = selectedIds.size;
    const bouncedSelected = recipients.filter(r => r.email_bounced && selectedIds.has(r.id)).length;

    return (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="publish-modal">
            <div className="bento p-6 max-w-3xl w-full my-8" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-2 gap-3">
                    <div className="min-w-0">
                        <span className="label-tag text-bayside">— Pubblicazione</span>
                        <h3 className="text-2xl font-display tracking-tighter mt-1">Conferma destinatari convocazione</h3>
                        <p className="text-xs text-zinc-500 mt-1 font-grotesk truncate">{assembly.title} · {fmtDate(assembly.date_time)}</p>
                    </div>
                    <button onClick={onClose} className="text-zinc-500 hover:text-white shrink-0"><X className="h-5 w-5" /></button>
                </div>

                <div className="mt-5 flex flex-wrap items-center gap-2">
                    <div className="flex-1 min-w-[200px] flex items-center gap-2 bg-zinc-900/40 rounded-xl px-3 py-2">
                        <Search className="h-4 w-4 text-zinc-500" />
                        <input
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            placeholder="Cerca per nome o email…"
                            className="bg-transparent border-0 outline-none flex-1 text-sm text-white placeholder:text-zinc-600"
                            data-testid="publish-recipients-search"
                        />
                    </div>
                    <select value={quotaFilter} onChange={e => setQuotaFilter(e.target.value)} className="bg-zinc-900/40 border-0 rounded-xl px-3 py-2 text-sm" data-testid="publish-quota-filter">
                        <option value="all">Tutte le quote</option>
                        <option value="A">Quota A</option>
                        <option value="B">Quota B</option>
                        <option value="C">Quota C</option>
                        <option value="D">Quota D</option>
                    </select>
                </div>

                <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs">
                    <div className="flex flex-wrap items-center gap-2 text-zinc-500">
                        <span>Visibili: <span className="text-white">{filtered.length}</span></span>
                        <span>· Selezionati: <span className="text-magenta font-semibold" data-testid="publish-selected-count">{selectedCount}</span> / {recipients.length}</span>
                        {bouncedSelected > 0 && (
                            <span className="text-vermillion flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> {bouncedSelected} con email in bounce</span>
                        )}
                    </div>
                    <div className="flex gap-2 flex-wrap">
                        <button onClick={selectAllVisible} className="btn-ghost text-xs" data-testid="publish-select-all">Seleziona visibili</button>
                        <button onClick={deselectAllVisible} className="btn-ghost text-xs" data-testid="publish-deselect-all">Deseleziona visibili</button>
                        <button onClick={resetToActive} className="btn-ghost text-xs" data-testid="publish-reset-active">Ripristina (soci attivi)</button>
                    </div>
                </div>

                <div className="mt-4 bg-zinc-950/40 rounded-2xl border border-zinc-900 max-h-[420px] overflow-y-auto" data-testid="publish-recipients-list">
                    {loading ? (
                        <div className="p-12 text-center text-zinc-500"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
                    ) : filtered.length === 0 ? (
                        <div className="p-8 text-center text-zinc-500 text-sm">Nessun destinatario per il filtro corrente.</div>
                    ) : (
                        <ul className="divide-y divide-zinc-900">
                            {filtered.map(r => (
                                <li key={r.id} className="flex items-center gap-3 p-3 hover:bg-zinc-900/40 transition" data-testid={`publish-row-${r.id}`}>
                                    <input
                                        type="checkbox"
                                        checked={selectedIds.has(r.id)}
                                        onChange={() => toggle(r.id)}
                                        className="accent-magenta h-4 w-4"
                                        data-testid={`publish-check-${r.id}`}
                                    />
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm text-white truncate">{r.nome}</div>
                                        <div className="text-[11px] text-zinc-500 font-mono truncate flex items-center gap-2">
                                            <Mail className="h-3 w-3 shrink-0" /> {r.email || "— nessuna email —"}
                                            {r.email_bounced && (
                                                <span className="text-vermillion flex items-center gap-1">
                                                    <AlertTriangle className="h-3 w-3" /> bounce
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex gap-1 shrink-0">
                                        {r.quota_categories.map(c => (
                                            <span key={c} className="px-1.5 py-0.5 rounded text-[10px] bg-magenta/10 text-magenta font-mono">{c}</span>
                                        ))}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <div className="mt-5 flex flex-wrap items-center justify-end gap-2">
                    {bouncedCount > 0 && (
                        <p className="text-[11px] text-zinc-500 mr-auto font-grotesk max-w-md">
                            <AlertTriangle className="h-3 w-3 inline text-vermillion mr-1" />
                            {bouncedCount} soci hanno l'email in bounce — esclusi dalla selezione iniziale ma puoi forzarli.
                        </p>
                    )}
                    <button onClick={onClose} className="btn-ghost text-sm" data-testid="publish-cancel">Annulla</button>
                    <button onClick={publish} disabled={publishing || selectedCount === 0} className="btn-rebel" data-testid="publish-confirm">
                        {publishing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                        Pubblica e invia a {selectedCount} {selectedCount === 1 ? "socio" : "soci"}
                    </button>
                </div>
            </div>
        </div>
    );
}


function VerbaleInlineBtn({ assembly, onDone }) {
    const [uploading, setUploading] = useState(false);
    const [linked, setLinked] = useState(assembly.verbale_document_id || null);
    const inputId = `verbale-inline-input-${assembly.id}`;

    const upload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 20 * 1024 * 1024) { toast.error("Il file supera i 20 MB"); e.target.value = ""; return; }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const r = await apiPrivate.post(`/admin/assemblies/${assembly.id}/verbale-upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            setLinked(r.data.document_id);
            toast.success(linked ? "Verbale sostituito" : "Verbale caricato — ora è visibile ai soci in Documenti");
            onDone?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Errore upload verbale");
        } finally {
            setUploading(false);
            e.target.value = "";
        }
    };

    return (
        <label
            htmlFor={inputId}
            className={`btn-ghost text-xs cursor-pointer ${linked ? "text-bayside" : "text-magenta"}`}
            data-testid={`verbale-inline-btn-${assembly.id}`}
            title={linked ? "Sostituisci verbale caricato" : "Carica il verbale dell'assemblea"}
        >
            {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />}
            {linked ? "Verbale ✓" : "Carica verbale"}
            <input
                id={inputId}
                type="file"
                accept="application/pdf"
                className="hidden"
                onChange={upload}
                disabled={uploading}
                data-testid={`verbale-inline-input-${assembly.id}`}
            />
        </label>
    );
}
